import React from 'react';
import type { HorseSocialBehavior } from '../../../../types/horseProfile';

interface SocialBehaviorEditFormProps {
  initialData: HorseSocialBehavior;
  onSubmit: (data: HorseSocialBehavior) => Promise<void>;
}

const DEFAULT_SOCIAL_BEHAVIOR: HorseSocialBehavior = {
  temperament: [],
  socialWithHorses: 'good',
  socialWithHumans: 'good',
  handlingNotes: '',
  preferences: [],
  behavioralIssues: []
};

export const SocialBehaviorEditForm: React.FC<SocialBehaviorEditFormProps> = ({
  initialData = DEFAULT_SOCIAL_BEHAVIOR,
  onSubmit
}) => {
  const [formData, setFormData] = React.useState<HorseSocialBehavior>({
    ...DEFAULT_SOCIAL_BEHAVIOR,
    ...initialData
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const addPreference = () => {
    setFormData(prev => ({
      ...prev,
      preferences: [
        ...prev.preferences,
        { category: '', description: '' }
      ]
    }));
  };

  const removePreference = (index: number) => {
    setFormData(prev => ({
      ...prev,
      preferences: prev.preferences.filter((_, i) => i !== index)
    }));
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          Temperament
        </label>
        <input
          type="text"
          value={formData.temperament.join(', ')}
          onChange={(e) => setFormData({
            ...formData,
            temperament: e.target.value.split(',').map(s => s.trim()).filter(Boolean)
          })}
          className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
          placeholder="Mit Komma getrennt eingeben (z.B. ruhig, ausgeglichen, sensibel)"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Sozialverhalten mit Pferden
          </label>
          <select
            value={formData.socialWithHorses}
            onChange={(e) => setFormData({
              ...formData,
              socialWithHorses: e.target.value as HorseSocialBehavior['socialWithHorses']
            })}
            className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
          >
            <option value="excellent">Ausgezeichnet</option>
            <option value="good">Gut</option>
            <option value="fair">Mittelmäßig</option>
            <option value="poor">Schwierig</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Sozialverhalten mit Menschen
          </label>
          <select
            value={formData.socialWithHumans}
            onChange={(e) => setFormData({
              ...formData,
              socialWithHumans: e.target.value as HorseSocialBehavior['socialWithHumans']
            })}
            className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
          >
            <option value="excellent">Ausgezeichnet</option>
            <option value="good">Gut</option>
            <option value="fair">Mittelmäßig</option>
            <option value="poor">Schwierig</option>
          </select>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          Umgangshinweise
        </label>
        <textarea
          value={formData.handlingNotes}
          onChange={(e) => setFormData({ ...formData, handlingNotes: e.target.value })}
          rows={3}
          className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
        />
      </div>

      <div>
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-sm font-medium text-gray-900 dark:text-white">Vorlieben & Abneigungen</h4>
          <button
            type="button"
            onClick={addPreference}
            className="px-3 py-1 text-sm text-indigo-600 hover:text-indigo-500 dark:text-indigo-400 dark:hover:text-indigo-300"
          >
            + Hinzufügen
          </button>
        </div>

        <div className="space-y-4">
          {formData.preferences.map((pref, index) => (
            <div key={index} className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Kategorie
                  </label>
                  <input
                    type="text"
                    value={pref.category}
                    onChange={(e) => {
                      const newPrefs = [...formData.preferences];
                      newPrefs[index] = { ...pref, category: e.target.value };
                      setFormData({ ...formData, preferences: newPrefs });
                    }}
                    className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Beschreibung
                  </label>
                  <input
                    type="text"
                    value={pref.description}
                    onChange={(e) => {
                      const newPrefs = [...formData.preferences];
                      newPrefs[index] = { ...pref, description: e.target.value };
                      setFormData({ ...formData, preferences: newPrefs });
                    }}
                    className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
                    required
                  />
                </div>
              </div>

              <div className="mt-4 flex justify-end">
                <button
                  type="button"
                  onClick={() => removePreference(index)}
                  className="text-sm text-red-600 hover:text-red-500 dark:text-red-400 dark:hover:text-red-300"
                >
                  Entfernen
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          Verhaltensauffälligkeiten
        </label>
        <input
          type="text"
          value={formData.behavioralIssues.join(', ')}
          onChange={(e) => setFormData({
            ...formData,
            behavioralIssues: e.target.value.split(',').map(s => s.trim()).filter(Boolean)
          })}
          className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
          placeholder="Mit Komma getrennt eingeben"
        />
      </div>
    </form>
  );
};